<!-- C:\platforma-frontend-admin-creator\app-subdomain-creators\src\views\CreatorLogin.vue -->
<template>
  <div
    class="bg-[#fafbfc] font-['Poppins'] min-h-screen flex flex-col justify-between bg-no-repeat"
    :style="{
      backgroundImage: `url(${require('@/assets/media/images/free-signup-survey-bg.svg')})`,
      backgroundPosition: 'center 170%',
      backgroundSize: '110% auto',
    }"
  >
    <!-- Header Section -->
    <div class="w-full p-4 sm:p-6 md:p-10 flex flex-col sm:flex-row justify-between mx-auto items-start sm:items-center gap-4 sm:gap-0">
      <div>
        <img
          class="h-8 w-auto"
          src="@/assets/media/images/platforma-logo-in-color.svg"
          alt="Platforma Logo"
        >
        <h3 class="text-sm font-medium text-zinc-800">
          The everything app for subscriptions.
        </h3>
      </div>

      <div class="flex gap-2 items-center w-full sm:w-auto justify-between sm:justify-start">
        <h3 class="text-[13px] sm:text-sm font-semibold text-zinc-800">
          Don't Have an account?
        </h3>

        <button
          class="header-login-button ml-3.5 h-[42px] w-[101px] bg-[#2D8CFF] rounded-[9px] text-sm font-semibold text-white shadow-[0_4px_14px_0_rgba(34,85,198,0.31)] duration-300 hover:ease-in-out hover:bg-[#1B67C2] hidden md:block"
          @click="navigateToSignup"
        >
          Sign Up
        </button>
      </div>
    </div>

    <!-- Login Form Section -->
    <div
      class="flex flex-1 flex-col justify-start px-4 sm:px-6 lg:px-8 mt-8 sm:mt-12 md:mt-20"
      style="flex-grow: 1;"
    >
      <div
        class="mx-auto w-full sm:w-full md:w-[500px] border border-zinc-100 rounded-lg shadow-md py-8 sm:py-10 md:py-12 px-5 sm:px-8 md:px-[75px] bg-white"
      >
        <h1 class="text-center text-[28px] sm:text-[30px] md:text-[32px] font-semibold">
          Welcome back!
        </h1>

        <form
          class="space-y-6 mt-5"
          @submit.prevent="handleLogin"
        >
          <!-- Email -->
          <div v-if="!mfaChallenge" class="text-left">
            <label
              for="email"
              class="block text-xs font-medium leading-6 text-gray-900"
            >Email address</label>
            <div class="mt-1 relative">
              <input
                id="email"
                v-model="email"
                name="email"
                type="email"
                placeholder="Enter Your Work Email"
                required
                class="pl-12 outline-none block w-full rounded-lg border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-zinc-400 placeholder:text-sm focus:ring-2 focus:ring-inset focus:ring-[#2d8bff] sm:text-sm sm:leading-6"
              >
              <img
                class="absolute h-5 w-auto text-zinc-400 top-2.5 left-4"
                src="@/assets/media/images/email-input-icon.svg"
              >
            </div>
          </div>

          <!-- Password -->
          <div v-if="!mfaChallenge" class="text-left">
            <label
              for="password"
              class="block text-xs font-medium leading-6 text-gray-900"
            >Password</label>
            <div class="mt-1 relative">
              <input
                id="password"
                v-model="password"
                name="password"
                :type="inputType"
                placeholder="Enter Password"
                required
                class="pl-12 outline-none block w-full rounded-lg border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#2d8bff] sm:text-sm sm:leading-6"
              >
              <img
                class="absolute h-5 w-auto text-zinc-400 top-2.5 left-4"
                src="@/assets/media/images/password-lock-input-icon.svg"
              >
              <EyeIcon
                v-if="!seePassword"
                class="absolute h-5 w-5 text-zinc-400 right-4 top-2.5 cursor-pointer"
                @click="togglePasswordViewer"
              />
              <EyeSlashIcon
                v-else
                class="absolute h-5 w-5 text-[#2d8bff] right-4 top-2.5 cursor-pointer"
                @click="togglePasswordViewer"
              />
            </div>
            <div class="mt-2 flex justify-end">
              <button
                type="button"
                class="text-xs font-medium text-[#2d8bff] hover:underline"
                @click="navigateToPasswordRecover"
              >
                Forgot Password?
              </button>
            </div>
          </div>

          <div v-if="mfaChallenge" class="text-left">
            <div class="mb-5 rounded-xl border border-blue-100 bg-blue-50 p-4">
              <p class="text-sm font-semibold text-zinc-900">Authenticator verification</p>
              <p class="mt-1 text-xs leading-5 text-zinc-600">Enter the 6-digit code from Google Authenticator, or use one of your recovery codes.</p>
            </div>
            <template v-if="!useRecoveryCode">
              <label class="block text-xs font-medium leading-6 text-gray-900">Authenticator code</label>
              <div class="mt-2 grid grid-cols-6 gap-2" @paste.prevent="handleMfaPaste">
                <input
                  v-for="(_, index) in mfaDigits"
                  :key="index"
                  :ref="(element) => setMfaInputRef(element, index)"
                  :value="mfaDigits[index]"
                  type="text"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  maxlength="1"
                  :aria-label="`Authenticator digit ${index + 1}`"
                  class="h-14 min-w-0 rounded-xl border-0 p-0 text-center text-xl font-semibold tabular-nums text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 outline-none focus:ring-2 focus:ring-inset focus:ring-[#2D8CFF]"
                  @input="handleMfaDigitInput(index, $event)"
                  @keydown="handleMfaKeydown(index, $event)"
                >
              </div>
            </template>
            <template v-else>
              <label for="mfa-code" class="block text-xs font-medium leading-6 text-gray-900">Recovery code</label>
              <input id="mfa-code" v-model.trim="mfaCode" name="mfa-code" type="text" autocomplete="one-time-code" placeholder="XXXX-XXXX-XXXX-XXXX" required autofocus class="mt-2 block w-full rounded-xl border-0 px-4 py-3 text-center font-mono text-sm font-semibold tracking-wider text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-[#2d8bff]">
            </template>
            <button type="button" class="mt-4 w-full text-center text-xs font-semibold text-[#2D8CFF] hover:text-[#1B67C2]" @click="toggleRecoveryMode">{{ useRecoveryCode ? 'Use authenticator code' : 'Use a recovery code' }}</button>
          </div>

          <p v-if="errorMessage" class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{{ errorMessage }}</p>

          <!-- Button -->
          <div>
            <button
              type="submit"
              :disabled="isSubmitting"
              class="flex w-full justify-center rounded-lg bg-[#2d8bff] px-3 py-[14px] text-sm font-bold leading-6 text-white shadow-sm hover:bg-[#2d8bffbd] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2d8bff] disabled:cursor-wait disabled:opacity-70"
            >
              {{ isSubmitting ? 'Signing in…' : (mfaChallenge ? 'Verify and log in' : 'Log In') }}
            </button>
            <button
              v-if="mfaChallenge"
              type="button"
              class="mt-3 w-full text-center text-xs font-semibold text-zinc-500 hover:text-zinc-800"
              @click="resetMfaStep"
            >
              Back to password
            </button>
          </div>
        </form>
      </div>

      <!-- Sign up link -->
      <div class="w-full mx-auto mt-8 sm:mt-10 text-center pb-6 sm:pb-0">
        <h3 class="text-sm font-normal text-zinc-600 sm:text-zinc-100">
          Don't have an account?
          <a
            href="https://platformarea.com/free-signup"
            class="text-sm font-normal text-zinc-700 sm:text-zinc-100 hover:underline"
            rel="noopener noreferrer"
          >
            Sign up
          </a>
        </h3>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, nextTick, ref, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useReCaptcha } from 'vue-recaptcha-v3';
import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline';

const isDev = process.env.NODE_ENV === 'development';
const baseUrl = isDev
  ? process.env.VUE_APP_API_PROXY_URL // http://localhost:5000
  : '';

// --- State and router setup
const email = ref('');
const password = ref('');
const mfaCode = ref('');
const mfaChallenge = ref('');
const mfaDigits = ref(['', '', '', '', '', '']);
const useRecoveryCode = ref(false);
const mfaInputRefs = [];
const errorMessage = ref('');
const isSubmitting = ref(false);
const seePassword = ref(false);
const route = useRoute();
const router = useRouter();
const loginTransition = inject('creatorLoginTransition', null);
const recaptcha = useReCaptcha();
const inputType = computed(() => (seePassword.value ? 'text' : 'password'));

const togglePasswordViewer = () => {
  seePassword.value = !seePassword.value;
};

const safeText = (value, maxLen = 256) => String(value == null ? '' : value).trim().slice(0, maxLen);

const normalizeBrands = (input) => (
  Array.isArray(input)
    ? input.map((item) => ({
      brand: safeText(item?.brand, 80),
      version: safeText(item?.version, 40),
    })).filter((item) => item.brand)
    : []
);

const collectClientContext = async () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return null;

  const uaData = navigator.userAgentData || null;
  let highEntropy = {};
  if (uaData && typeof uaData.getHighEntropyValues === 'function') {
    try {
      highEntropy = await uaData.getHighEntropyValues([
        'platformVersion',
        'architecture',
        'bitness',
        'model',
        'uaFullVersion',
        'fullVersionList',
        'wow64',
      ]);
    } catch (_error) {
      highEntropy = {};
    }
  }

  const payload = {
    userAgent: safeText(navigator.userAgent, 1024),
    platform: safeText(navigator.platform, 120),
    language: safeText(navigator.language, 32),
    timezone: safeText(Intl.DateTimeFormat().resolvedOptions().timeZone, 120),
    uaData: {
      mobile: typeof uaData?.mobile === 'boolean' ? uaData.mobile : null,
      platform: safeText(uaData?.platform, 120),
      platformVersion: safeText(highEntropy?.platformVersion || uaData?.platformVersion, 80),
      model: safeText(highEntropy?.model || uaData?.model, 120),
      architecture: safeText(highEntropy?.architecture || uaData?.architecture, 60),
      bitness: safeText(highEntropy?.bitness || uaData?.bitness, 40),
      wow64: typeof highEntropy?.wow64 === 'boolean' ? highEntropy.wow64 : null,
      brands: normalizeBrands(uaData?.brands),
      fullVersionList: normalizeBrands(highEntropy?.fullVersionList || uaData?.fullVersionList),
    },
  };

  const hasData = Boolean(
    payload.userAgent
    || payload.platform
    || payload.language
    || payload.timezone
    || payload.uaData.platform
    || payload.uaData.platformVersion
    || payload.uaData.model
    || payload.uaData.brands.length
    || payload.uaData.fullVersionList.length
  );

  return hasData ? payload : null;
};

// --- Login logic (from production)
const handleLogin = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    errorMessage.value = '';
    const isMfaStep = Boolean(mfaChallenge.value);
    if (isMfaStep && !useRecoveryCode.value && mfaCode.value.length !== 6) {
      errorMessage.value = 'Enter all six authenticator digits.';
      return;
    }
    if (!isMfaStep && !recaptcha.executeRecaptcha) {
      console.error('reCAPTCHA is not initialized properly.');
      errorMessage.value = 'Security verification is still loading. Please try again.';
      return;
    }

    const recaptchaToken = isMfaStep ? '' : await recaptcha.executeRecaptcha('login_form');

    const clientContext = isMfaStep ? null : await collectClientContext();
    const response = await fetch(`${baseUrl}/api/auth/${isMfaStep ? 'login/mfa' : 'login'}`, {

      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(isMfaStep
        ? { mfaChallenge: mfaChallenge.value, code: mfaCode.value }
        : {
          email: email.value,
          password: password.value,
          recaptchaToken,
          ...(clientContext ? { clientContext } : {}),
        }),
      credentials: 'include', // keeps cookies in sync
    });

    const payload = await response.json().catch(() => ({}));

    if (response.status === 202 && payload.requiresMfa) {
      mfaChallenge.value = payload.mfaChallenge;
      mfaCode.value = '';
      mfaDigits.value = ['', '', '', '', '', ''];
      setTimeout(() => focusMfaDigit(0), 0);
      return;
    }

    if (response.ok) {
      localStorage.removeItem('auth');
      try {
        const continuePath = String(route.query?.continue || '');
        const postLoginTarget = continuePath.startsWith('/creator/') && !continuePath.startsWith('//')
          ? continuePath
          : { name: 'Dashboard' };
        await router.replace(postLoginTarget);
        if (router.currentRoute.value.name === 'Login') {
          errorMessage.value = 'We could not open your workspace. Please try signing in again.';
          return;
        }
        await nextTick();
        const transitionStartedAt = loginTransition?.start();
        if (transitionStartedAt) await loginTransition.finish(transitionStartedAt);
      } catch (navigationError) {
        loginTransition?.cancel();
        throw navigationError;
      }
    } else {
      errorMessage.value = payload.message || 'Invalid credentials';
    }
  } catch (error) {
    console.error('Login error:', error);
    errorMessage.value = 'An error occurred while logging in. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};

const resetMfaStep = () => {
  mfaChallenge.value = '';
  mfaCode.value = '';
  errorMessage.value = '';
  mfaDigits.value = ['', '', '', '', '', ''];
  useRecoveryCode.value = false;
};

const setMfaInputRef = (element, index) => { if (element) mfaInputRefs[index] = element; };
const syncMfaCode = () => { mfaCode.value = mfaDigits.value.join(''); };
const focusMfaDigit = (index) => { mfaInputRefs[Math.max(0, Math.min(5, index))]?.focus(); };
const handleMfaDigitInput = (index, event) => {
  const value = String(event?.target?.value || '').replace(/\D/g, '');
  if (value.length > 1) {
    const next = [...mfaDigits.value];
    value.slice(0, 6 - index).split('').forEach((digit, offset) => { next[index + offset] = digit; });
    mfaDigits.value = next;
    syncMfaCode();
    focusMfaDigit(Math.min(5, index + value.length));
    return;
  }
  mfaDigits.value[index] = value.slice(-1);
  syncMfaCode();
  if (value && index < 5) focusMfaDigit(index + 1);
};
const handleMfaKeydown = (index, event) => {
  if (event.key === 'Backspace' && !mfaDigits.value[index] && index > 0) focusMfaDigit(index - 1);
  if (event.key === 'ArrowLeft' && index > 0) { event.preventDefault(); focusMfaDigit(index - 1); }
  if (event.key === 'ArrowRight' && index < 5) { event.preventDefault(); focusMfaDigit(index + 1); }
};
const handleMfaPaste = (event) => {
  const digits = String(event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6).split('');
  if (!digits.length) return;
  mfaDigits.value = Array.from({ length: 6 }, (_, index) => digits[index] || '');
  syncMfaCode();
  focusMfaDigit(Math.min(5, digits.length));
};
const toggleRecoveryMode = () => {
  useRecoveryCode.value = !useRecoveryCode.value;
  mfaCode.value = '';
  mfaDigits.value = ['', '', '', '', '', ''];
  if (!useRecoveryCode.value) setTimeout(() => focusMfaDigit(0), 0);
};

// --- External navigation
const navigateToSignup = () => {
  window.location.href = 'https://platformarea.com/free-signup';
};

const navigateToPasswordRecover = () => {
  router.push({ name: 'CreatorPasswordRecover' });
};

// --- ReCAPTCHA badge handling
onMounted(() => {
  document.body.classList.add('show-recaptcha-badge');
});

onUnmounted(() => {
  document.body.classList.remove('show-recaptcha-badge');
});

</script>








  
  
